/**
 * In-memory breadcrumb ring buffer (telemetry).
 *
 * The ONE place suite UIs keep a short trail of what the user just did —
 * navigations, failed requests, uncaught errors — so a support ticket can carry
 * that context for the supporter. It is a passive sink: it stores only what
 * callers feed it through `recordBreadcrumb` and adds NO global listeners, no
 * network calls and no logging side effects of its own. The host app wires its
 * EXISTING hooks (router guard, axios interceptor, error handler) to feed it;
 * see workspace-ui's router/api/main.
 */

export type BreadcrumbType = 'nav' | 'request_error' | 'error';

/**
 * A recorded breadcrumb: its `type`, when it happened (`at`, epoch ms set by the
 * buffer) plus any caller-supplied fields (e.g. `to`, `url`, `status`,
 * `message`).
 */
export interface Breadcrumb {
  type: BreadcrumbType;
  at: number;
  [key: string]: unknown;
}

export interface BreadcrumbSnapshot {
  page: string;
  items: Breadcrumb[];
}

// Cap keeps the buffer a bounded, recent trail — older entries fall off the
// front (ring buffer). ~30 captures the last handful of actions before a
// failure without growing unbounded.
const CAP = 30;
const buffer: Breadcrumb[] = [];

/**
 * Append a breadcrumb. `type` is required; any other fields are kept verbatim.
 * The `at` timestamp is always stamped here so callers never have to. Once the
 * buffer is full the oldest entries are dropped.
 */
export function recordBreadcrumb(entry: { type: BreadcrumbType; [key: string]: unknown }): void {
  if (!entry || !entry.type) return;
  buffer.push({ ...entry, at: Date.now() });
  if (buffer.length > CAP) buffer.splice(0, buffer.length - CAP);
}

/**
 * Snapshot the current trail plus the active page path. Returns shallow copies
 * so the caller can attach the result to a payload without aliasing the live
 * buffer.
 */
export function snapshotBreadcrumbs(): BreadcrumbSnapshot {
  const page = typeof window !== 'undefined' ? window.location.pathname : '';
  return { page, items: buffer.map((b) => ({ ...b })) };
}
